import React, { useMemo } from 'react';
import qs from 'qs';
import get from 'lodash/get';
import { routes } from 'constants/config';
import useFetch from 'components/shared/hooks/useFetch';
import BlankSlate from 'components/shared/index/blankslate';
import PageTitle from 'components/shared/PageTitle';
import noContentSrc from 'images/no_content.svg';
import errorSrc from 'images/error.svg';

import ElementTable from './ElementTable/ElementTable';

const DEFAULT_SORT = { direction: 'asc', column: 'title' };
const DEFAULT_PAGE = 1;
const DEFAULT_SEARCH = qs.stringify(
  { sort: DEFAULT_SORT, page: DEFAULT_PAGE },
  { encodeValuesOnly: true, addQueryPrefix: true },
);

const PHASES = {
  ERROR: 'ERROR',
  SUCCESS: 'SUCCESS',
  EMPTY: 'EMPTY',
  LOADING: 'LOADING',
};

function getSortFromSortType(sortType) {
  if (sortType === 'numeric') {
    return 'desc';
  }

  return 'asc';
}

function getPhase({ data, loading, error }) {
  if (loading) {
    return PHASES.LOADING;
  }

  if (error) {
    return PHASES.ERROR;
  }

  if (data.elements.length < 1) {
    return PHASES.EMPTY;
  }

  return PHASES.SUCCESS;
}

const useQueryParams = search => {
  // We place api filters to the url and use them to set initial table filters
  return useMemo(() => {
    const { sort, page } = qs.parse(search, { ignoreQueryPrefix: true });
    return { sort, page: parseInt(page, 10) || 1 };
  }, [search]);
};

const setQueryParams = (history, params) => {
  history.push({
    search: qs.stringify(params, { encodeValuesOnly: true }),
  });
};

const NoContent = () => (
  <div className="ContentLibrary--blankslate">
    <BlankSlate
      image={noContentSrc}
      title={t('contents.library.element_library.no_content.title')}
      description={t('contents.library.element_library.no_content.description')}
    />
  </div>
);

const Error = () => (
  <div className="ContentLibrary--blankslate">
    <BlankSlate
      image={errorSrc}
      title={t('contents.library.element_library.error.title')}
      description={t('contents.library.element_library.error.description')}
    />
  </div>
);

const ElementLibrary = ({ location: { search: urlSearch }, history }) => {
  const search = urlSearch || DEFAULT_SEARCH;
  const fetchData = useFetch(`${routes.CONTENT.LIBRARY_ELEMENTS}${search}`);
  const phase = getPhase(fetchData);
  const { data = { elements: [] }, loading } = fetchData;
  const { sort, page } = useQueryParams(search);
  const perPage = get(data, 'filters.perPage');

  const setSort = ({ column, sortType }) => {
    const { direction: sortDirection, column: sortColumn } = sort;
    let newSort = { direction: getSortFromSortType(sortType), column };
    if (column === sortColumn) {
      newSort = {
        direction: sortDirection === 'desc' ? 'asc' : 'desc',
        column,
      };
    }

    setQueryParams(history, { sort: newSort, page, perPage });
  };

  const setPage = newPage => {
    setQueryParams(history, { sort, page: newPage, perPage });
  };

  const setPerPage = newPerPage => {
    setQueryParams(history, { sort, page: 1, perPage: newPerPage });
  };

  return (
    <>
      <PageTitle title={t('contents.library.title')} />
      <div className="ContentLibrary--container">
        {(phase === PHASES.LOADING || phase === PHASES.SUCCESS) && (
          <ElementTable
            loading={loading}
            elements={data.elements}
            sort={sort}
            page={page}
            perPage={perPage}
            totalPages={data.totalPages}
            setSort={setSort}
            setPage={setPage}
            setPerPage={setPerPage}
          />
        )}
        {phase === PHASES.EMPTY && <NoContent />}
        {phase === PHASES.ERROR && <Error />}
      </div>
    </>
  );
};

export default ElementLibrary;
